import {
  isEmpty,
  isPlainObject,
  isArray,
  differenceWith,
  isEqual,
} from 'lodash';

/**
 * This function compares two objects and returns only what the second object have that is different from the first one
 *
 * @param currentObject Record<string, unknown>
 * @param compareObject? Record<string, unknown>
 * @category Formatters
 * @category Object
 */
export const objectDiff = (
  currentObject: Record<string, unknown>,
  compareObject?: Record<string, unknown>,
) => {
  if (!compareObject || !currentObject) {
    return {};
  }

  const diff: Record<string, unknown> = {};
  const currentObjectKeys = Object.keys(currentObject) as Array<
    keyof Record<string, unknown>
  >;
  const compareObjectKeys = Object.keys(compareObject) as Array<
    keyof Record<string, unknown>
  >;
  const compareObjectKeysString = compareObjectKeys.toString();

  const hasObjectsAnyRelation = currentObjectKeys.some((key) =>
    compareObjectKeysString.includes(key),
  );

  if (!hasObjectsAnyRelation) {
    return {};
  }

  const allKeys = [...new Set([...currentObjectKeys, ...compareObjectKeys])];

  allKeys.forEach((key) => {
    // Arrays has typeof === 'object'
    if (
      currentObject[key] &&
      isPlainObject(compareObject[key]) &&
      isPlainObject(currentObject[key])
    ) {
      diff[key] = objectDiff(
        currentObject[key] as Record<string, unknown>,
        compareObject[key] as Record<string, unknown>,
      );

      if (isEmpty(diff[key])) {
        delete diff[key];
      }
    } else if (isArray(compareObject[key]) && isArray(currentObject[key])) {
      const compareArraysCompareObjectWithCurrent = differenceWith(
        compareObject[key] as Array<unknown>,
        currentObject[key] as Array<unknown>,
        isEqual,
      );
      const compareArraysCurrentWithCompareObject = differenceWith(
        currentObject[key] as Array<unknown>,
        compareObject[key] as Array<unknown>,
        isEqual,
      );
      const hasDifference =
        compareArraysCompareObjectWithCurrent.length > 0 ||
        compareArraysCurrentWithCompareObject.length > 0;

      if (hasDifference) {
        diff[key] = compareObject[key];
      }
    } else if (
      typeof compareObject[key] !== 'undefined' &&
      !Object.is(compareObject[key], currentObject[key])
    ) {
      diff[key] = compareObject[key];
    } else {
      delete diff[key];
    }
  });

  return diff;
};
